import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExportButton from "components/button/ExportButton";
import LabelValueDisplay from "components/LabelValueDisplay";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import useLoadMore from "hooks/useLoadMore";
import { EstQuoteDetailTire, ITireItemDetail, PrcFeesData } from "features/car_model/types";
import TextFieldComponent from "components/input/TextFieldComponent";
import { MESSAGE } from "constants/validate";
import { formatCurrency } from "utils";
import { styled } from "@mui/material/styles";
import Notify from "config/notify";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import FilterProcFeeModal from "features/mst_proc_fee/components/FilterProcFeeModal";
import TireListTable from "features/mst_proc_fee/components/TireListTable";
import useProcFeeList from "features/mst_proc_fee/hooks/useProcFeeList";
import {
  CASHBACK_PERIOD_OPTIONS,
  PUNCTURE_COMPENSATION_OPTIONS,
} from "features/mst_proc_fee/constants/options";
import { useTirePriceCalculator } from "features/mst_proc_fee/hooks/useTirePriceCalculator";

const StyledTableCell = styled(TableCell)({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "20px",
  backgroundColor: "var(--primary-500)",
  width: "calc((100% - 8px) / 5)",
});

const StyledBodyTableCell = styled(TableCell)({
  textAlign: "center",
  fontSize: "20px",
  width: "210px",
  padding: "11px 20px",
});

const styles = {
  label: {
    fontSize: { xs: 16, md: 18, lg: 20 },
    backgroundColor: "var(--primary-500)",
    textAlign: "center",
    padding: "10px 0",
    fontWeight: "bold",
  },
};

const tireTypeOptions = [
  { label: "夏タイヤ", value: "s" },
  { label: "冬タイヤ", value: "w" },
  { label: "オールシーズン", value: "a" },
];

/**
 *
 */
function ProcFeeList() {
  const {
    procFees,
    setProcFees,
    tireList,
    makerList,
    categoryList,
    selectedCategory,
    setSelectedCategory,
    setTireList,
    loading,
    onLoadMore,
    handleFilter,
    selectedTireIds,
    setSelectedTireIds,
    selectedMaker,
    setSelectedMaker,
    mstProcFee,
    page,
    totalSelectedTire,
    setTotalSelectedTire,
    recommendFlag,
    setRecommendFlag,
    punctureProtection,
    tireCategory,
    maxSelectedTires,
  } = useProcFeeList();
  const [tireNumber, setTireNumber] = useState(
    (Array.isArray(mstProcFee?.tbl_quote_detail_tire) &&
      mstProcFee.tbl_quote_detail_tire[0]?.quantity) ||
      4,
  );
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const [punctureCompensation, setPunctureCompensation] = useState(
    mstProcFee?.cashback?.puncture || 0,
  );
  const [cashbackPeriod, setCashbackPeriod] = useState(mstProcFee?.cashback?.cashback_period || 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stockFilterStore, setStockFilterStore] = useState(false);
  const [stockFilterDepotVendor, setStockFilterDepotVendor] = useState(false);
  const [isDisposalChecked, setIsDisposalChecked] = useState(
    mstProcFee?.proc_fees?.waste_tire_cost?.checked === undefined
      ? true
      : mstProcFee?.proc_fees?.waste_tire_cost?.checked,
  );
  const [isValveChecked, setIsValveChecked] = useState(
    mstProcFee?.proc_fees?.rubber_stem_valve_cost?.checked === undefined
      ? true
      : mstProcFee?.proc_fees?.rubber_stem_valve_cost?.checked,
  );
  const [isNitrogenChecked, setIsNitrogenChecked] = useState(
    mstProcFee?.proc_fees?.nitrogen_inflation_cost?.checked === undefined
      ? false
      : mstProcFee?.proc_fees?.nitrogen_inflation_cost?.checked,
  );
  const [isHubProtectionChecked, setIsHubProtectionChecked] = useState(
    mstProcFee?.proc_fees?.hub_rust_prevention_cost?.checked === undefined
      ? false
      : mstProcFee?.proc_fees?.hub_rust_prevention_cost?.checked,
  );
  const navigate = useNavigate();

  const [params, setParams] = useState({
    replace_labor_cost: 0,
    waste_tire_cost_flag: 0,
    rubber_stem_valve_flag: 0,
    nitrogen_inflation_flag: 0,
    hub_rust: 0,
  });

  const prevSite = sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_FROM) || "";
  const maker = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_MAKER) || "{}");
  const carTireListByCode: ITireItemDetail[] = JSON.parse(
    sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "{}",
  );

  const tireCategoryText = useMemo<string>(
    () => tireTypeOptions.find((item) => item.value === tireCategory)?.label || "",
    [tireCategory],
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFilterClick = () => {
    setIsModalOpen(true);
  };

  const handleDisposalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDisposalChecked(event.target.checked);
  };

  const handleValveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsValveChecked(event.target.checked);
  };

  const handleNitrogenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsNitrogenChecked(event.target.checked);
  };

  const handleHubProtectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsHubProtectionChecked(event.target.checked);
  };

  const handleBlur = (field: string, e: React.FocusEvent<HTMLDivElement>) => {
    const newParams = { ...params, [field]: +e.target.innerText.replace(",", "") };
    setParams(newParams);
  };

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof MstProcFeeTireItem) => {
      const numericValue = e.target.value.replace(/[^0-9]/g, "");
      setProcFees((prev) => ({
        ...prev,
        [fieldName]: Number(numericValue),
      }));
    },
    [setProcFees],
  );

  const onSelectTire = (event: React.ChangeEvent<HTMLInputElement>, tireId: string) => {
    if (totalSelectedTire >= maxSelectedTires && event.target.checked) {
      Notify.setNotification({
        show: true,
        title: `${maxSelectedTires}個までしか選択できません`,
        actionsPosition: "end",
      });
      return;
    }
    if (event.target.checked) {
      setTotalSelectedTire((prev: number) => prev + 1);
    } else {
      setTotalSelectedTire((prev: number) => prev - 1);
    }
    setSelectedTireIds((prev: Record<string, boolean>) => ({
      ...prev,
      [tireId]: event.target.checked,
    }));
  };

  // Use centralized calculator
  const { calculator } = useTirePriceCalculator({
    tireNumber,
    procFees,
    isDisposalChecked,
    isValveChecked,
    isNitrogenChecked,
    isHubProtectionChecked,
    punctureCompensation,
    cashbackPeriod,
    punctureProtection,
    forceStandardPricing: true, // Always use standard pricing (list_price/a_price) in ProcFeeList
  });

  const navigateConfirmResult = (type: "tire" | "wheel") => {
    if (Object.values(selectedTireIds).every((item) => item === false)) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
        actionsPosition: "center",
      });
      return;
    }
    if (Object.values(errors).some((item) => item)) {
      return;
    }
    const sessionData: Record<string, any> = {};
    if (procFees) {
      sessionData.proc_fees = Object.keys(procFees).reduce((acc, key) => {
        switch (key) {
          case "replac_labor_cost":
            acc[key] = {
              value: procFees[key],
              checked: true,
            };
            break;
          case "waste_tire_cost":
            acc[key] = {
              value: procFees[key],
              checked: isDisposalChecked,
            };
            break;
          case "rubber_stem_valve_cost":
            acc[key] = {
              value: procFees[key],
              checked: isValveChecked,
            };
            break;
          case "nitrogen_inflation_cost":
            acc[key] = {
              value: procFees[key],
              checked: isNitrogenChecked,
            };
            break;
          case "hub_rust_prevention_cost":
            acc[key] = {
              value: procFees[key],
              checked: isHubProtectionChecked,
            };
            break;
          default:
            break;
        }
        return acc;
      }, {} as PrcFeesData);
    }
    const detail: Partial<EstQuoteDetailTire>[] = [];
    tireList.forEach((tire) => {
      if (!selectedTireIds[String(tire.id)]) return;
      const result = calculator(tire);
      const {
        basePrice,
        discountedPrice,
        wage,
        totalPrice,
        compensation,
        totalPriceWithCompensation,
      } = result;
      detail.push({
        checked: true,
        tire_item_cd: tire.item_cd,
        maker_name: tire.maker_name,
        item_name: tire.item_name,
        tire_loin_speed_mark: tire.tire_loin_speed_mark,
        tire_label: tire.tire_label,
        quantity: tireNumber,
        nomal_price: basePrice,
        fixed_discount: Number(tire.fixed_discount),
        special_discount: Number(tire.special_discount),
        discounted_sale_price: discountedPrice,
        wage_cost_price: wage,
        wage_cost_in_price: totalPrice,
        puncture_protection_price: compensation,
        puncture_protection_in_price: totalPriceWithCompensation,
        cb_tire_price: tire.cb_tire_price,
        cb_wheel_price: tire.cb_wheel_price,
        confirm_total_price: totalPriceWithCompensation,
        remarks: tire.remarks || "",
      });
    });
    sessionData.tbl_quote_detail_tire = detail;
    sessionData.cashback = {
      puncture: punctureCompensation,
      cashback_period: cashbackPeriod,
    };

    // save to storage
    sessionStorage.setItem(SESSION_KEY.RECOMMEND_FLAG, JSON.stringify(recommendFlag));
    sessionStorage.setItem(SESSION_KEY.CURRENT_PAGE, String(page));
    if (type === "wheel") {
      sessionStorage.setItem(SESSION_KEY.SELECTED_TIRE, JSON.stringify(sessionData));
      sessionStorage.setItem(SESSION_KEY.TIRE_WHEEL_TIRE_FROM, routes.mstProcFee);

      // TODO: Clear wheel store state and selected wheel since this is a fresh selection flow
      sessionStorage.removeItem(SESSION_KEY.WHEEL_STORE_STATE);

      navigate(routes.mstTireWheelStore);
    } else {
      sessionStorage.setItem(SESSION_KEY.SELECTED_TIRE, JSON.stringify(sessionData));
      navigate(routes.mstCarModelResult);
    }
  };

  const renderSelectedCar = useMemo(() => {
    if (Object.keys(maker).length === 0) return "";
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { car_model_and_period, modl_name } = maker;
    if (!car_model_and_period || !modl_name) return "";

    if (car_model_and_period.includes(modl_name)) {
      const periodPart = car_model_and_period.replace(modl_name, "").trim();
      return (
        <>
          {modl_name} <br />
          {periodPart}
        </>
      );
    }
    return car_model_and_period;
  }, [maker?.car_model_and_period, maker?.modl_name]);

  useEffect(() => {
    if ([1, 2].includes(tireNumber)) {
      setPunctureCompensation(0);
      setCashbackPeriod(1);
    }
  }, [tireNumber]);

  /** 店舗 在庫 と 倉庫/仕入先 在庫 の OR フィルタ。両方 false のときは無効。 */
  const filteredTireList = useMemo(() => {
    if (!stockFilterStore && !stockFilterDepotVendor) return tireList;
    return tireList.filter((tire) => {
      const storeMatch = stockFilterStore && (tire.store_quantity ?? 0) > 0;
      const depotVendorMatch =
        stockFilterDepotVendor &&
        ((tire.depot_quantity ?? 0) > 0 || (tire.vendor_centor_quantity ?? 0) > 0);
      return storeMatch || depotVendorMatch;
    });
  }, [tireList, stockFilterStore, stockFilterDepotVendor]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            marginBottom: "20px",
            columnGap: "20px",
            rowGap: "10px",
          }}
        >
          <Box sx={{ width: { xs: "100%", xl: "calc((100% - 80px) *3 / 5)" } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: 28, md: 31, lg: 33 },
                fontWeight: "medium",
                color: "var(--gray-700)",
                mb: { xs: "20px", lg: "40px" },
              }}
            >
              タイヤを選ぶ
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", lg: "280px" },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {prevSite === "maker" ? (
                  <img
                    src={maker.car_image_url}
                    alt={maker.car_model_and_period}
                    width="163px"
                    style={{ height: "98px" }}
                  />
                ) : null}
              </Box>
              <Box sx={{ display: "flex", columnGap: "4px", flexWrap: "wrap", flex: 1 }}>
                {[
                  { label: "選択車両", value: renderSelectedCar },
                  {
                    label: "タイヤサイズ",
                    value:
                      maker?.selectedTire?.mmm_car_tiresize ||
                      carTireListByCode[0]?.tire_size ||
                      "",
                  },
                  { label: "タイヤ種類", value: tireCategoryText },
                  { label: "サイズ指定", value: maker?.selectedTire?.mmm_inch },
                ].map((item) => (
                  <LabelValueDisplay
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    type="text"
                    containerSx={{ width: "calc((100% - 4px) / 2)" }}
                    labelSx={styles.label}
                    valueSx={{
                      textAlign: "center",
                      margin: "5px auto",
                      fontSize: { xs: 16, md: 18, lg: 20 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: { xs: "100%", xl: "calc((100% - 80px) *2 / 5)" } }}>
            <Typography
              sx={{
                textAlign: "right",
                fontWeight: "medium",
                fontSize: { xs: "28px", md: "31px" },
                color: "var(--gray-700)",
                mb: 1.5,
              }}
            >
              工賃設定
            </Typography>
            <Typography
              sx={{
                color: "var(--gray-700)",
                textAlign: "right",
                fontWeight: "600",
                fontSize: { xs: 12, md: 14, lg: 16 },
              }}
            >
              1本あたりの税込金額
            </Typography>

            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table
                sx={{
                  borderCollapse: "unset",
                  borderSpacing: "2px",
                  boxSizing: "border-box",
                  minWidth: 695,
                }}
              >
                <TableHead>
                  <TableRow sx={{ height: "48px" }}>
                    <StyledTableCell
                      sx={{
                        backgroundColor: "var(--green-600)",
                        border: "1px solid var(--green-600)",
                        whiteSpace: "nowrap",
                        py: "2px",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      }}
                    >
                      交換工賃
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        border: "1px solid",
                        backgroundColor: isDisposalChecked
                          ? "var(--green-600)"
                          : "var(--offwhite-500)",
                        borderColor: isValveChecked ? "var(--green-600)" : "var(--offwhite-500)",
                        py: "2px",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Checkbox
                          checked={isDisposalChecked}
                          onChange={handleDisposalChange}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 28 } },
                            padding: 0,
                          }}
                        />
                        処分
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        border: "1px solid",
                        backgroundColor: isValveChecked
                          ? "var(--green-600)"
                          : "var(--offwhite-500)",
                        borderColor: isValveChecked ? "var(--green-600)" : "var(--offwhite-500)",
                        whiteSpace: "nowrap",
                        px: 1,
                        py: "2px",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Checkbox
                          checked={isValveChecked}
                          onChange={handleValveChange}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 28 } },
                            padding: 0,
                          }}
                        />
                        エアバルブ
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        border: "1px solid",
                        backgroundColor: isNitrogenChecked
                          ? "var(--green-600)"
                          : "var(--offwhite-500)",
                        borderColor: isNitrogenChecked ? "var(--green-600)" : "var(--offwhite-500)",
                        py: "2px",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Checkbox
                          checked={isNitrogenChecked}
                          onChange={handleNitrogenChange}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 28 } },
                            padding: 0,
                          }}
                        />
                        窒素
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        border: "1px solid",
                        whiteSpace: "nowrap",
                        backgroundColor: isHubProtectionChecked
                          ? "var(--green-600)"
                          : "var(--offwhite-500)",
                        borderColor: isHubProtectionChecked
                          ? "var(--green-600)"
                          : "var(--offwhite-500)",
                        py: "2px",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Checkbox
                          checked={isHubProtectionChecked}
                          onChange={handleHubProtectionChange}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: { xs: 20, md: 28 } },
                            padding: 0,
                          }}
                        />
                        ハブ防錆
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <StyledBodyTableCell
                      sx={{ p: 0 }}
                      onBlur={(e) => handleBlur("replace_labor_cost", e)}
                    >
                      <TextFieldComponent
                        type="string"
                        value={formatCurrency(procFees?.replac_labor_cost as number)}
                        sxInput={{
                          fontSize: { xs: 16, md: 18, lg: 20 },
                          height: "48px",
                          textAlign: "center",
                        }}
                        sxInputDisable={{
                          color: "var(--black)",
                          WebkitTextFillColor: "var(--black)",
                        }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          textAlign: "center",
                          color: "var(--black)",
                          border: !errors.replac_labor_cost
                            ? "2px solid var(--silver-100)"
                            : "2px solid var(--red-800)",
                        }}
                        sxNotchedOutline={{ border: "none" }}
                        normalizeHalfWidth
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(9),
                            value: 9,
                          },
                        }}
                        isHiddenError
                        onInputText={(e) => onChangeInput(e, "replac_labor_cost")}
                        onError={(error) =>
                          setErrors((pre) => ({ ...pre, replac_labor_cost: error }))
                        }
                      />
                    </StyledBodyTableCell>
                    <StyledBodyTableCell
                      sx={{ p: 0 }}
                      onBlur={(e) => handleBlur("waste_tire_cost_flag", e)}
                    >
                      <TextFieldComponent
                        type="string"
                        value={formatCurrency(procFees?.waste_tire_cost as number)}
                        sxInput={{
                          fontSize: { xs: 16, md: 18, lg: 20 },
                          height: "48px",
                          textAlign: "center",
                        }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          border: !errors.waste_tire_cost
                            ? "2px solid var(--silver-100)"
                            : "2px solid var(--red-800)",
                        }}
                        sxNotchedOutline={{ border: "none" }}
                        normalizeHalfWidth
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(9),
                            value: 9,
                          },
                        }}
                        isHiddenError
                        onInputText={(e) => onChangeInput(e, "waste_tire_cost")}
                        onError={(error) =>
                          setErrors((pre) => ({ ...pre, waste_tire_cost: error }))
                        }
                      />
                    </StyledBodyTableCell>
                    <StyledBodyTableCell sx={{ p: 0 }} onBlur={(e) => handleBlur("valve", e)}>
                      <TextFieldComponent
                        type="string"
                        value={formatCurrency(procFees?.rubber_stem_valve_cost as number)}
                        sxInput={{
                          fontSize: { xs: 16, md: 18, lg: 20 },
                          height: "48px",
                          textAlign: "center",
                        }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          border: !errors.rubber_stem_valve_cost
                            ? "2px solid var(--silver-100)"
                            : "2px solid var(--red-800)",
                        }}
                        sxNotchedOutline={{ border: "none" }}
                        normalizeHalfWidth
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(9),
                            value: 9,
                          },
                        }}
                        isHiddenError
                        onInputText={(e) => onChangeInput(e, "rubber_stem_valve_cost")}
                        onError={(error) =>
                          setErrors((pre) => ({ ...pre, rubber_stem_valve_cost: error }))
                        }
                      />
                    </StyledBodyTableCell>
                    <StyledBodyTableCell
                      sx={{ p: 0 }}
                      onBlur={(e) => handleBlur("nitrogen_inflation_flag", e)}
                    >
                      <TextFieldComponent
                        type="string"
                        value={formatCurrency(procFees?.nitrogen_inflation_cost as number)}
                        sxInput={{
                          fontSize: { xs: 16, md: 18, lg: 20 },
                          height: "48px",
                          textAlign: "center",
                        }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          border: !errors.nitrogen_inflation_cost
                            ? "2px solid var(--silver-100)"
                            : "2px solid var(--red-800)",
                        }}
                        sxNotchedOutline={{ border: "none" }}
                        normalizeHalfWidth
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(9),
                            value: 9,
                          },
                        }}
                        isHiddenError
                        onInputText={(e) => onChangeInput(e, "nitrogen_inflation_cost")}
                        onError={(error) =>
                          setErrors((pre) => ({ ...pre, nitrogen_inflation_cost: error }))
                        }
                      />
                    </StyledBodyTableCell>
                    <StyledBodyTableCell sx={{ p: 0 }} onBlur={(e) => handleBlur("hub_rust", e)}>
                      <TextFieldComponent
                        type="string"
                        value={formatCurrency(procFees?.hub_rust_prevention_cost as number)}
                        sxInput={{
                          fontSize: { xs: 16, md: 18, lg: 20 },
                          height: "48px",
                          textAlign: "center",
                        }}
                        sxRoot={{
                          borderRadius: 0,
                          width: "100%",
                          border: !errors.hub_rust_prevention_cost
                            ? "2px solid var(--silver-100)"
                            : "2px solid var(--red-800)",
                        }}
                        sxNotchedOutline={{ border: "none" }}
                        normalizeHalfWidth
                        validateOption={{
                          maxLength: {
                            message: MESSAGE.MAX_LENGTH(9),
                            value: 9,
                          },
                        }}
                        isHiddenError
                        onInputText={(e) => onChangeInput(e, "hub_rust_prevention_cost")}
                        onError={(error) =>
                          setErrors((pre) => ({ ...pre, hub_rust_prevention_cost: error }))
                        }
                      />
                    </StyledBodyTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "end",
                marginTop: "10px",
                columnGap: "4px",
              }}
            >
              <Box sx={{ width: { xs: "100%", md: "calc(60% - 4px)" } }}>
                <Box
                  sx={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    fontWeight: "bold",
                    height: "48px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "var(--offwhite-500)",
                  }}
                >
                  パンク補償
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={punctureCompensation}
                    onChange={(e) => setPunctureCompensation(Number(e.target.value))}
                    sx={{ columnGap: "10px", paddingX: "10px" }}
                  >
                    {PUNCTURE_COMPENSATION_OPTIONS.map((item) => (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        disabled={tireNumber !== 4}
                        control={<Radio />}
                        label={
                          <Typography
                            sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, color: "var(--black)" }}
                          >
                            {item.label}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Box>
              <Box sx={{ width: { xs: "100%", md: "40%" } }}>
                <Box
                  sx={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    fontWeight: "bold",
                    height: "48px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "var(--offwhite-500)",
                  }}
                >
                  キャッシュバック
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={cashbackPeriod}
                    onChange={(e) => setCashbackPeriod(Number(e.target.value))}
                    sx={{ columnGap: "10px", paddingX: "10px" }}
                  >
                    {CASHBACK_PERIOD_OPTIONS.map((item) => (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        control={<Radio />}
                        disabled={tireNumber !== 4}
                        label={
                          <Typography
                            sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, color: "var(--black)" }}
                          >
                            {item.label}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: "var(--offwhite-100)",
          p: { xs: 2, md: 2.5, lg: 5 },
          pt: { xl: "24px !important" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: "20px",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "9px", rowGap: "20px" }}>
            <ExportButton
              label="選択した商品を見積表へ"
              sx={{
                whiteSpace: "nowrap",
                width: { xs: 250, md: 328 },
                fontSize: { xs: 16, md: 20, lg: 23 },
              }}
              onClick={() => navigateConfirmResult("tire")}
            />
            {/* {prevSite === "maker" && (
              <ExportButton
                label="選択した商品にホイールを組み合わせる"
                sx={{
                  whiteSpace: "nowrap",
                  width: { xs: 360, md: 485 },
                  fontSize: { xs: 16, md: 20, lg: 23 },
                }}
                onClick={() => navigateConfirmResult("wheel")}
              />
            )} */}
          </Box>
          <Box sx={{ display: "flex", columnGap: "20px" }}>
            <ExportButton
              sx={{ fontSize: { xs: 16, md: 20, lg: 23 }, width: { xs: 110, md: 180 } }}
              label="絞込"
              onClick={handleFilterClick}
            />
          </Box>
        </Box>
        <TireListTable
          tireList={filteredTireList}
          tireNumber={tireNumber}
          selectedTireIds={selectedTireIds}
          cashbackPeriod={cashbackPeriod}
          procFees={procFees}
          errors={errors}
          calculator={calculator}
          onSelectTire={onSelectTire}
          onTireNumberChange={setTireNumber}
          onTireListChange={setTireList}
          onErrorChange={(field, error) => setErrors((prev) => ({ ...prev, [field]: error }))}
        />
        {renderNodeLoader()}
      </Box>
      {isModalOpen && (
        <FilterProcFeeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          makerList={makerList}
          selectedMaker={selectedMaker}
          setSelectedMaker={setSelectedMaker}
          categoryList={categoryList}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          recommendFlag={recommendFlag}
          setRecommendFlag={setRecommendFlag}
          stockFilterStore={stockFilterStore}
          stockFilterDepotVendor={stockFilterDepotVendor}
          handleFilter={async (values) => {
            if (values) {
              setStockFilterStore(values.stockFilterStore ?? false);
              setStockFilterDepotVendor(values.stockFilterDepotVendor ?? false);
            }
            await handleFilter(values);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export default ProcFeeList;
